import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../../../contexts/user-context/user-context';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
  DialogTitle,
  DialogContent,
  TablePagination
} from '@mui/material';
import axios, { AxiosResponse, isAxiosError } from 'axios';
import { API_ENDPOINT } from '../../../../../configurations/global.config';
import { useSnackBar } from '../../../../../contexts/snackbar';
import ConfirmAction from '../../../../common/confirm-action/confirm-action';
import { AddLink, Cancel, Check, CheckCircle, Checklist, ContentCopy, Delete, DeleteForever, Link, ReplayOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import { IWebhook, IWebhookQueueItem } from '../../../../../interfaces/common/webhooks';
import GenericTable from '../../../../common/generic-table/generic-table';
import { IconWithText } from '../../../../common/styles/components';
import { emptyCustomer } from '../../../../../data/customers';
import { emptyWebhook } from '../../../../../data/webhooks';
import CustomDialog from '../../../../common/custom-dialog/custom-dialog';
import { isMobile, isTablet } from 'react-device-detect';
import WebhookForm from '../webhook-form';
import { ErrorsFlattenner } from '../../../../common/errors-flattenner/errors-flattenner';
import { green, red } from '@mui/material/colors';
import { PaginationContext } from '../../../../../contexts/pagination-context';
import CopyLinkButton from '../../../../common/copy-link-button/copy-link-button';
import dayjs from 'dayjs';
import { DoubleDate } from '../../../../common/double-date-picker/double-date-picker.interfaces';
import FilterChips from '../../../../common/filter-chips/filter-chips';
import DoubleDatePicker from '../../../../common/double-date-picker/double-date-picker';
import { format } from 'date-fns';

enum DateFilterInputMode {
  TODAY = 'Today',
  LAST_7_DAYS = 'Last 7 Days',
  CUSTOM = 'Custom Range',
}

function WebhooksDeadLetterListing() {
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();
  const [deadletter, setDeadletter] = useState<IWebhookQueueItem[]>()
  const [deadletterCount, setDeadletterCount] = useState<number>(0)
  const [webhooks, setWebhooks] = useState<IWebhook[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [response, setResponse] = useState<AxiosResponse>()
  const [retryingWebhook, setRetryingWebhook] = useState<number>()
  const [isPurging, setIsPurging] = useState(false)

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);
    
  const [selectedWebhookNames, setSelectedWebhookNames] = useState<string[]>([]);
  const [filterMode, setFilterMode] = useState<DateFilterInputMode>(DateFilterInputMode.TODAY);
  const [dateFilter, setDateFilter] = useState<DoubleDate>({
      startDate: dayjs(),
      endDate: dayjs(),
    });
    
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
  });

  const disableAll = isPurging || (retryingWebhook != undefined && retryingWebhook > 0)

  useEffect(() => {
    const fetchWebhooks = async () => {
      const endpoint = `${API_ENDPOINT}/v1/webhooks/`;
      try { 
        const response = await axios.get<IWebhook[]>(endpoint, config);
        setWebhooks(response.data);
      } catch {
        showMessage("Failed to load webhooks", 'error');
      }
    };
    fetchWebhooks();
  }, []);
  
  useEffect(() => {
      if (filterMode === DateFilterInputMode.TODAY) {
        setDateFilter({
          startDate: dayjs(),
          endDate: dayjs(),
        });
      }
  
      if (filterMode === DateFilterInputMode.LAST_7_DAYS) {
        setDateFilter({
          startDate: dayjs().subtract(7, 'day'),
          endDate: dayjs(),
        });
      }
    }, [filterMode]);

   const fetchDeadletterData = async () => {
        let endpoint = `${API_ENDPOINT}/v1/webhooks/invocations/deadletter/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}`
        if (dateFilter?.startDate)
            endpoint += `&date_from=${encodeURIComponent(dateFilter.startDate.format('YYYY-MM-DD'))}`;
        if (dateFilter?.endDate)
            endpoint += `&date_to=${encodeURIComponent(dateFilter.endDate.format('YYYY-MM-DD'))}`;
        if (selectedWebhookNames.length > 0) {
            endpoint += `&webhook=${encodeURIComponent(selectedWebhookNames.join(","))}`;
        }

        setIsLoading(true);
        try {
            const response = await axios.get<{count: number, results: IWebhookQueueItem[]}>(endpoint, config)
            setDeadletterCount(response.data.count)
            setDeadletter(response.data.results)
        } catch(e) {
            if (isAxiosError(e)) {
                setResponse(e.response)
            }

            showMessage('Failed to load dead letter queue', 'error')
        } finally {
            setIsLoading(false)
        }
    }

  useEffect(()=>{
    fetchDeadletterData()
  }, [JSON.stringify(selectedWebhookNames), JSON.stringify(controller), JSON.stringify(dateFilter)])

  const handleForceRetry = async (id: number) => {
    setRetryingWebhook(id)
    try {
        await axios.post(`${API_ENDPOINT}/v1/webhooks/invocations/deadletter/${id}/retry/`, {}, config)
        showMessage("Success!")
    } catch(e) {
        if (isAxiosError(e) && e.status == 417) {
            showMessage("Unsuccessful attempt.", 'error')
            setDeadletter(deadletter!.map(item => item.id == id ? {...item, retry_count: item.retry_count + 1} : item))
        } else {
            showMessage("Internal Server Error", 'error')
        }
    } finally {
        setRetryingWebhook(undefined)
    }
  }

  const purgeQueue = async () => {
    setIsPurging(true)
    try {
        await axios.post(`${API_ENDPOINT}/v1/webhooks/invocations/deadletter/purge/`, {}, config)
        showMessage("Purged Successfully!")
        setDeadletter([])
        setDeadletterCount(0)
    } catch(e) {
        showMessage("Operation Failed.", 'error')
    } finally {
        setIsPurging(false)
    }
  }

   const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

   const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

    return <div>
       <Alert sx={{mb:2}} severity='info'> Dead letter queue shows all webhook invocation attempts that failed despite retrying multiple times.</Alert>

         {response && response.status >= 300 && (
                    <Alert sx={{mb:4}} severity="error" >
                        Failed to save webhook
                        <ErrorsFlattenner rawErrors={response.data} />
                    </Alert>      
                  )}
        
          <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: "space-between", }}>
    
                        <FilterChips
          label={''}
          options={Array.from(Object.values(DateFilterInputMode))}
          selected={[filterMode]}
          onChange={(selected) => {
            if (selected.length > 0)
              setFilterMode(selected[0] as DateFilterInputMode);
          }}
        />

                {webhooks && <FilterChips
                label={''}
                options={webhooks.map(item=>item.name)}
                selected={selectedWebhookNames}
                multiple
                onChange={(selected) => {
                    setSelectedWebhookNames(selected);
                }}
                />}
                        
                        <Box sx={{
                            display: "flex",
                        }}>

                            <Button
                                variant='outlined'
                                disabled={isLoading}
                                onClick={purgeQueue}
                                sx={{ml: 1}}
                                color='error'
                                startIcon={isLoading ? <CircularProgress size={16} /> : <DeleteForever />}
                                >
                                Purge
                            </Button>

                            <Button
                                variant='outlined'
                                disabled={isLoading}
                                onClick={fetchDeadletterData}
                                sx={{ml: 1}}
                                startIcon={isLoading ? <CircularProgress size={16} /> : <ReplayOutlined />}
                                >
                                Refresh Data
                            </Button>

                        </Box>
                        
                </Box>
        </Box>

        {filterMode == DateFilterInputMode.CUSTOM && (
                <div style={{ margin: '15px', maxWidth: 550 }}>
                  <DoubleDatePicker
                    defaultDoubleDate={dateFilter}
                    headless
                    disableClear
                    dateFilterAction={(d: DoubleDate) => setDateFilter(d)}
                  />
                </div>
              )}
        
        
        <Chip
            icon={<Checklist />}
            color="primary"
            sx={{mb :2}}
            label={`${deadletterCount} Items`}
            variant={'outlined'}
        />



        <GenericTable 
            sourceView="webhooks-deadletter-listing"
            items={deadletter ?? []} 
            selectedItems={new Map} 
            isLoading={isLoading} 
            fullWidth
            columns={[
                {
                    id: "id",
                    header: "ID",
                    display_body: (w: IWebhookQueueItem) => <div style={{textDecoration: "underline", cursor: "pointer"}}><b>{w.id}</b></div>
                },
                {
                    id: "Enqueued At",
                    header: "Enqueued At",
                    display_body: (w: IWebhookQueueItem) => format(w.created_at, "dd-MM-yyyy HH:mm:ss")
                },
                {
                    id: "webhook",
                    header: "Name",
                    display_body: (w: IWebhookQueueItem) => w.webhook.name
                },
                {
                    id: "endpoint",
                    header: "Endpoint",
                    display_body: (w: IWebhookQueueItem) => w.webhook.endpoint,
                },
                {
                    id: "related_order",
                    header: "Related Order",
                    display_body: (w: IWebhookQueueItem) => <>
                    <a target='_blank' rel='noreferrer' href={`/order/${w.object_id}`}>
                        <b>1{w.object_id}</b>
                    </a>
                </>
                },
                {
                    id: "retries",
                    header: "Retries",
                    display_body: (w: IWebhookQueueItem) => w.retry_count
                },
                {
                    id: "manual-retry",
                    header: "Force Retry",
                    display_body: (w: IWebhookQueueItem) => <Button disabled={disableAll} onClick={()=>handleForceRetry(w.id)}>Retry <>{retryingWebhook == w.id && <CircularProgress sx={{ml: 3}} size={20}/>} </></Button>
                }
            ]} 
            getItemUID={(w)=>w.id.toString()} 
            />

            <TablePagination
                component="div"
                onPageChange={handlePageChange}
                page={controller.page}
                count={deadletterCount}
                rowsPerPage={controller.rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
                labelRowsPerPage=""
                style={{ overflow: 'visible' }}
            />

                   
    </div>
}

export default WebhooksDeadLetterListing;
